"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import Image from "next/image"
import { getTranslations } from "@/lib/translations"
import type { Locale } from "@/lib/i18n"

const images = {
  "/web-design.jpg": "/web-design.jpg",
  "/web-programlama.jpg": "/web-programlama.jpg",
  "/mobile-design.jpg": "/mobile-design.jpg",
  "/e-commerce.jpg": "/e-commerce.jpg",
  "/ai.jpg": "/ai.jpg",
}

type HeroProps = {
  locale: Locale
}

// AnimatePresence initial={false}: the first slide is visible straight away (it is the LCP
// element); only later slide changes animate
export function Hero({ locale }: HeroProps) {
  const t = getTranslations(locale)
  const heroSlides = t.hero.slides.map((slide, index) => ({
    id: index + 1,
    ...slide,
    image: ["/web-design.jpg", "/web-programlama.jpg", "/mobile-design.jpg", "/e-commerce.jpg", "/ai.jpg"][index],
  }))
  const [currentIndex, setCurrentIndex] = useState(0)
  const [displayedText, setDisplayedText] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const currentSlide = heroSlides[currentIndex]

  // Typewriter effect
  useEffect(() => {
    setDisplayedText("")
    setIsTyping(true)
    let charIndex = 0
    const text = currentSlide.description

    const typeInterval = setInterval(() => {
      if (charIndex < text.length) {
        setDisplayedText(text.slice(0, charIndex + 1))
        charIndex++
      } else {
        setIsTyping(false)
        clearInterval(typeInterval)
      }
    }, 30) // Her karakter 30ms'de yazılsın

    return () => clearInterval(typeInterval)
  }, [currentSlide.id])

  // Slide değişimi - Timer yönetimi
  useEffect(() => {
    // Önceki timer'ı temizle
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    
    // Yeni timer başlat
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % heroSlides.length)
    }, 20000) // 20 saniyede bir değişir

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [currentIndex]) // currentIndex değiştiğinde timer'ı resetle

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? heroSlides.length - 1 : prevIndex - 1
    )
  }

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === heroSlides.length - 1 ? 0 : prevIndex + 1
    )
  }

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 via-white to-slate-100 py-8 sm:py-12 lg:py-0">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 lg:pt-12 pb-8 sm:pb-12 lg:pb-20 lg:pb-24" suppressHydrationWarning>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start" suppressHydrationWarning>
          {/* Sol Taraf - Yazılar (Mobilde üstte, Desktop'ta solda) */}
          <div className="relative order-1 lg:order-1 h-[300px] sm:h-[400px] lg:h-[500px] xl:h-[600px] pt-4 sm:pt-8 lg:pt-16" suppressHydrationWarning>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentSlide.id}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="space-y-4 sm:space-y-6 lg:space-y-8 text-left relative z-10"
                suppressHydrationWarning
              >
                {/* Mobilde okunabilirlik için arka plan */}
                <div className="lg:hidden absolute inset-0 -my-4 bg-white/95 backdrop-blur-sm rounded-2xl -z-10 px-4 py-4 left-0 right-0" suppressHydrationWarning />
                
                {/* Badge */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-sm sm:text-base font-medium text-[rgb(3,74,166)] uppercase tracking-wider"
                >
                  {currentSlide.badge}
                </motion.p>
                
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-slate-900 leading-[1.1] text-left"
                  suppressHydrationWarning
                >
                  <span className="text-[rgb(3,74,166)]" suppressHydrationWarning>{currentSlide.highlight}</span>
                  <br />
                  <span className="text-slate-600" suppressHydrationWarning>{currentSlide.subtitle}</span>
                </motion.h1>

                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed text-left font-mono h-[3rem] sm:h-[4rem] lg:h-[5rem] overflow-hidden" suppressHydrationWarning>
                  {displayedText}
                  {isTyping && (
                    <span className="inline-block w-0.5 h-4 sm:h-5 lg:h-6 bg-[rgb(3,74,166)] ml-1 animate-pulse">|</span>
                  )}
                </p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4"
                  suppressHydrationWarning
                >
                  <Button 
                    asChild 
                    size="lg" 
                    className="bg-slate-900 hover:bg-slate-800 text-white px-5 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-5 text-sm sm:text-base h-auto rounded-lg font-medium shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
                  >
                    <Link href={`/${locale}/teklif-al`} className="flex items-center justify-center">
                      {t.hero.freeQuote}
                      <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                    </Link>
                  </Button>
                  <Button 
                    asChild 
                    size="lg" 
                    variant="outline" 
                    className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-700 px-5 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-5 text-sm sm:text-base h-auto rounded-lg font-medium w-full sm:w-auto"
                  >
                    <Link href={currentSlide.ctaLink} className="flex items-center justify-center">{t.hero.viewService}</Link>
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Sağ Taraf - Görsel (Mobilde altta, Desktop'ta sağda) */}
          <div className="relative order-2 lg:order-2 w-full h-[300px] sm:h-[400px] lg:h-[500px] xl:h-[600px] rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl" suppressHydrationWarning>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentSlide.id}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="absolute inset-0"
                suppressHydrationWarning
              >
                <Image
                  src={currentSlide.image}
                  alt={currentSlide.badge}
                  fill
                  priority={currentIndex === 0}
                  fetchPriority={currentIndex === 0 ? "high" : "auto"}
                  className="object-cover"
                  quality={currentIndex === 0 ? 85 : 75}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading={currentIndex === 0 ? "eager" : "lazy"}
                />
              </motion.div>
            </AnimatePresence>
            
            {/* Arrow Navigation - Sağ Alt Köşe */}
            <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 z-20 flex items-center gap-2" suppressHydrationWarning>
              <button
                onClick={goToPrevious}
                className="p-2 sm:p-2.5 bg-slate-200/90 hover:bg-slate-300/90 rounded-lg transition-all"
                aria-label={t.hero.prevSlide}
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-black stroke-[2]" />
              </button>
              <button
                onClick={goToNext}
                className="p-2 sm:p-2.5 bg-slate-200/90 hover:bg-slate-300/90 rounded-lg transition-all"
                aria-label={t.hero.nextSlide}
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-black stroke-[2]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
